import { Color } from '@persona-id/design-tokens';
import { MapPinIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import styled from 'styled-components';

import MapAndStreetView from 'dashboard/components/ReportResult/MapAndStreetView';
import TableLayout, {
  EmbeddedTableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { BusinessAddress, ReportBusinessLookup } from 'dashboard/models/report/business-lookup';
import Card from 'design-system/components/Card';
import * as Spacing from 'design-system/variables/spacing';

interface BusinessAddressesCardProps {
  report: ReportBusinessLookup;
}

const BusinessLookupAddresses = ({ report }: BusinessAddressesCardProps) => {
  const [previewAddressIndex, setPreviewAddressIndex] = useState(0);

  const {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    result: { addresses: addresses },
  } = report.attributes;

  const selectedAddress = addresses[previewAddressIndex];

  const body = (
    <>
      {selectedAddress && (
        <MapAndStreetView
          lat={selectedAddress.latitude}
          lng={selectedAddress.longitude}
          addressStreet1={selectedAddress['street-1']}
          addressStreet2={selectedAddress['street-2']}
          addressCity={selectedAddress['city']}
          addressSubdivision={selectedAddress['subdivision']}
          addressPostalCode={selectedAddress['postal-code']}
          data-chromatic='ignore'
        />
      )}
      <TableLayout
        containerMaxHeight={420}
        dataTest='addresses-table'
        showHeader={true}
        isEmpty={addresses.length === 0}
        nullState={
          <EmbeddedTableNullState marginVertical={Spacing.BASE * 2} dataTestBlock='addresses-table'>
            No addresses found
          </EmbeddedTableNullState>
        }
        headerTR={
          <>
            <StickyTableTH>{/* blank space for pin */}</StickyTableTH>
            <StickySecondTableTH>Street address</StickySecondTableTH>
            <StickyTableTH>City, state</StickyTableTH>
            <StickyTableTH>Postal code</StickyTableTH>
          </>
        }
      >
        {addresses.map((address: BusinessAddress, i: number) => {
          return (
            <TableTR
              id={`business-addresses-row-index-${i}`}
              key={i}
              onClick={setPreviewAddressIndex.bind(null, i)}
            >
              <FirstTableTD>
                {previewAddressIndex === i && (
                  <PinWrapper>
                    <RedPin />
                  </PinWrapper>
                )}
              </FirstTableTD>
              <SecondTableTD>{[address['street-1'], address['street-2']].join(' ')}</SecondTableTD>
              <TableTD>{[address['city'], address['subdivision']].join(', ')}</TableTD>
              <TableTD>{address['postal-code']}</TableTD>
            </TableTR>
          );
        })}
      </TableLayout>
    </>
  );

  return (
    <>
      <Card.Title>Addresses</Card.Title>
      {body}
    </>
  );
};

const PinWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const RedPin = styled(MapPinIcon)`
  width: 20px;
  height: 20px;
  color: ${Color.CoreRed500};
`;

const StickyTableTH = styled(TableTH)`
  position: sticky;
  top: 0;
`;

const StickySecondTableTH = styled(TableTH)`
  position: sticky;
  top: 0;
  padding-left: 8px;
`;

const FirstTableTD = styled(TableTD)`
  padding-right: 0px;
`;

const SecondTableTD = styled(TableTD)`
  padding-left: 8px;
`;

export default BusinessLookupAddresses;
